/**
 * Shared Menu Structure Configuration
 * 
 * Single source of truth for all menu items in the application.
 * Used by:
 * - ProjectSidebar.js (main navigation)
 * - MenuAccessModal.js (admin employee menu configuration)
 * 
 * When adding new menu items, add them here and they will automatically
 * appear in both the sidebar and the admin configuration modal.
 */

export const MENU_STRUCTURE = [
  {
    id: 'home',
    label: 'Home',
    icon: '🏠',
    department: 'projects'
  },
  {
    id: 'department-selector',
    label: 'Department',
    icon: '🏢',
    hideInProject: true,
    subItems: [
      { id: 'dept-projects', label: 'Project Management', departmentId: 'projects' },
      { id: 'dept-accounting', label: 'Accounting', departmentId: 'accounting' },
      { id: 'dept-estimation', label: 'Estimation', departmentId: 'estimation' },
      { id: 'dept-procurement', label: 'Procurement', departmentId: 'procurement' },
      { id: 'dept-resources', label: 'Resource Management', departmentId: 'resources' },
      { id: 'dept-hse', label: 'HSE', departmentId: 'hse' },
      { id: 'dept-pmo', label: 'PMO', departmentId: 'pmo' },
      { id: 'dept-quality', label: 'Quality', departmentId: 'quality' },
      { id: 'dept-operations', label: 'Operations', departmentId: 'operations' },
      { id: 'dept-admin', label: 'Admin', departmentId: 'admin' },
      { id: 'dept-sales', label: 'Sales', departmentId: 'sales' },
      { id: 'dept-tool-crib', label: 'Tool Crib', departmentId: 'tool-crib' }
    ]
  },
  {
    id: 'project-directory',
    label: 'Project Directory',
    icon: '📁',
    department: 'projects'
  },
  {
    id: 'actuals',
    label: 'Time Tracker',
    icon: '⏱️',
    hideInProject: true,
    department: 'accounting'
  },
  {
    id: 'coding-structure',
    label: 'Coding Structure',
    icon: '📋',
    hideInProject: true,
    department: 'accounting'
  },
  {
    id: 'hiring-layoff',
    label: 'Hiring / Layoff',
    icon: '👥',
    hideInProject: true,
    department: 'accounting'
  },
  {
    id: 'project-sharepoint',
    label: 'SharePoint',
    icon: '🔗',
    requiresProject: true,
    department: 'projects'
  },
  {
    id: 'project-autodesk-build',
    label: 'Autodesk Build',
    icon: '🏗️',
    requiresProject: true,
    department: 'projects'
  },
  {
    id: 'project-procore',
    label: 'Procore',
    icon: '🚧',
    requiresProject: true,
    department: 'projects'
  },
  {
    id: 'documentationManagement',
    label: "Doc's Mngmnt",
    icon: '📄',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'transmittal-log', label: 'Transmittal Log' },
      { id: 'technical-submittals', label: 'Submittals' },
      { id: 'technical-correspondence', label: 'Correspondence' }
    ]
  },
  // -------------------------------------------------------------------------
  // Estimation sidebar IA (Casper flow) — leaf IDs preserved for grants/routing.
  // 1 Estimate Log/SharePoint → 2 Detailed Estimation → 3 Discipline Summaries
  // → 4 Lookups → 5 Indirects → 6 Cost Assembly → 7 Bid Package
  // -------------------------------------------------------------------------
  {
    id: 'estimate-log',
    label: 'Estimate Log',
    icon: '📋',
    hideInProject: true,
    department: 'estimation'
  },
  {
    id: 'estimate-sharepoint',
    label: 'SharePoint',
    icon: '🔗',
    requiresEstimate: true,
    hideInProject: true,
    department: 'estimation'
  },
  {
    id: 'estimate-autodesk-build',
    label: 'Autodesk Build',
    icon: '🏗️',
    requiresEstimate: true,
    hideInProject: true,
    department: 'estimation'
  },
  {
    id: 'estimate-procore',
    label: 'Procore',
    icon: '🚧',
    requiresEstimate: true,
    hideInProject: true,
    department: 'estimation'
  },
  {
    id: 'estimation-detailed',
    label: 'Detailed Estimation',
    icon: '🔍',
    hideInProject: true,
    department: 'estimation',
    subItems: [
      {
        id: 'estimation-piping',
        label: 'Piping',
        requiresEstimate: true,
        subItems: [
          { id: 'mto', label: 'MTO (Detailed Estimation)' },
          { id: 'material-index', label: 'Material Index' },
          {
            id: 'estimation-piping-lookup-tables',
            label: 'Lookup Tables',
            subItems: [
              { id: 'estimation-piping-lt-part-rate-db', label: 'Part Rate DB' },
              { id: 'estimation-piping-lt-size', label: 'LT_Size' },
              { id: 'estimation-piping-lt-material', label: 'LT_Material' },
              { id: 'estimation-piping-lt-schedule', label: 'LT_Schedule' },
              { id: 'estimation-piping-lt-length', label: 'LT_Length' }
            ]
          },
          { id: 'estimation-piping-convertors', label: 'Convertors' },
          { id: 'shop-field-ratio', label: 'Shop/Field Ratio' },
          { id: 'estimation-piping-bom-extractor-api-2', label: 'BOM Extractor' },
          { id: 'estimation-piping-pid-extraction', label: 'P&ID Extraction' }
        ]
      },
      {
        id: 'estimation-mechanical',
        label: 'Mechanical',
        subItems: [
          { id: 'estimation-mechanical-equipment-history', label: 'Equipment History DB' },
          { id: 'estimation-mechanical-detailed-estimation', label: 'Detailed Estimation', requiresEstimate: true }
        ]
      },
      { id: 'estimation-structural-detailed-estimation', label: 'Structural', requiresEstimate: true },
      { id: 'estimation-civil-detailed-estimation', label: 'Civil', requiresEstimate: true },
      { id: 'estimation-electrical-detailed-estimation', label: 'Electrical', requiresEstimate: true },
      { id: 'estimation-hvac-detailed-estimation', label: 'HVAC', requiresEstimate: true },
      { id: 'estimation-sm-field-detailed-estimation', label: 'Sheetmetal Field', requiresEstimate: true },
      { id: 'estimation-sm-shop-detailed-estimation', label: 'Sheetmetal Shop', requiresEstimate: true },
      { id: 'estimation-tank-shop-detailed-estimation', label: 'Tank Fabrication', requiresEstimate: true },
      { id: 'estimation-boiler-mkr-detailed-estimation', label: 'Boiler Maker', requiresEstimate: true },
      { id: 'estimation-mr-detailed-estimation', label: 'M&R (Millwright)', requiresEstimate: true },
      { id: 'estimation-pipe-fab-detailed-estimation', label: 'Pipe Fabrication', requiresEstimate: true }
    ]
  },
  {
    id: 'estimation-discipline-summaries',
    label: 'Discipline Summaries',
    icon: '📊',
    hideInProject: true,
    department: 'estimation',
    requiresEstimate: true,
    subItems: [
      { id: 'estimation-piping-summary', label: 'Piping' },
      { id: 'estimation-mechanical-summary', label: 'Mechanical' },
      { id: 'estimation-structural-summary', label: 'Structural' },
      { id: 'estimation-civil-summary', label: 'Civil' },
      { id: 'estimation-electrical-summary', label: 'Electrical' },
      { id: 'estimation-hvac-summary', label: 'HVAC' },
      { id: 'estimation-sm-field-summary', label: 'Sheetmetal Field' },
      { id: 'estimation-sm-shop-summary', label: 'Sheetmetal Shop' },
      { id: 'estimation-tank-shop-summary', label: 'Tank Fabrication' },
      { id: 'estimation-boiler-mkr-summary', label: 'Boiler Maker' },
      { id: 'estimation-mr-summary', label: 'M&R (Millwright)' },
      { id: 'estimation-pipe-fab-summary', label: 'Pipe Fabrication' }
    ]
  },
  {
    id: 'estimation-lookups',
    label: 'Lookup Tables & Rates',
    icon: '📚',
    hideInProject: true,
    department: 'estimation',
    subItems: [
      { id: 'estimation-unit-rate', label: 'Unit Rate' },
      { id: 'estimation-part-rate-db', label: 'Part Rate DB' },
      { id: 'estimation-rate-benchmarks', label: 'Rate Benchmarks' },
      { id: 'estimation-labour-rates-bank', label: 'Labour Rates Library' },
      { id: 'estimation-indirects-bank', label: 'Indirects Library' },
      { id: 'estimation-site-expenses', label: 'Site Expenses Library' },
      { id: 'currency-exchange', label: 'Currency Exchange' }
    ]
  },
  {
    id: 'estimation-indirects-group',
    label: 'Indirects',
    icon: '👥',
    hideInProject: true,
    department: 'estimation',
    subItems: [
      { id: 'cost-breakdown-indirects', label: 'Indirects', requiresEstimate: true },
      { id: 'cost-breakdown-site-expenses', label: 'Site Expenses', requiresEstimate: true },
      { id: 'cost-breakdown-rentals', label: 'Rentals', requiresEstimate: true },
      { id: 'estimation-scheduling', label: 'Scheduling Estimation' }
    ]
  },
  {
    id: 'estimation-cost-assembly',
    label: 'Cost Assembly & CB Summary',
    icon: '💰',
    hideInProject: true,
    department: 'estimation',
    requiresEstimate: true,
    subItems: [
      { id: 'estimation-dashboard', label: 'Dashboard' },
      { id: 'cost-breakdown-summary', label: 'CB Summary' },
      { id: 'cost-breakdown-disciplines', label: 'Disciplines Overview' },
      { id: 'cost-breakdown-labour-rates', label: 'Labour Rates' },
      { id: 'estimation-crew-calculation', label: 'Crew Calcs' },
      { id: 'estimation-ai-curve', label: 'AI Accuracy Curve' }
    ]
  },
  {
    id: 'estimation-bid-package',
    label: 'Bid Package',
    icon: '📦',
    hideInProject: true,
    department: 'estimation',
    requiresEstimate: true,
    subItems: [
      { id: 'sov', label: 'SOV' },
      { id: 'cost-breakdown-bid-form', label: 'Bid Form' },
      { id: 'cost-breakdown-proposal-letter', label: 'Proposal Letter' },
      { id: 'cost-breakdown-clarifications', label: 'Clarifications' },
      { id: 'cost-breakdown-llm-clarification-letter', label: 'Clarification Letter' },
      { id: 'material-management-estimation', label: 'Solid Materials' },
      {
        id: 'estimation-proposal-schedule-group',
        label: 'Proposal Schedule',
        requiresEstimate: true,
        subItems: [
          { id: 'estimation-proposal-schedule', label: 'Proposal Schedule' },
          { id: 'estimation-evm-report', label: 'EVM Report', requiresEstimate: true },
          { id: 'estimation-schedule-modules', label: 'Schedule options', requiresEstimate: true },
          { id: 'estimation-lbs', label: 'LBS Schedule', requiresEstimate: true, requiresModule: 'lbs' },
          { id: 'estimation-schedule-model', label: 'Model', requiresEstimate: true }
        ]
      }
    ]
  },
  {
    id: 'progressTrackers',
    label: 'Progress Tracking',
    icon: '📊',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'piping-insulation', label: 'Piping' },
      { id: 'mechanical-rotating-eqp', label: 'Mechanical' },
      { id: 'structure', label: 'Structural' },
      { id: 'hvac', label: 'HVAC' },
      { id: 'mechanical-ducts', label: 'Ducts' },
      { id: 'boilermaker', label: 'Boilermaker' },
      { id: 'electrical', label: 'Electrical' },
      { id: 'civil', label: 'Civil' }
      // { id: 'change-orders', label: 'Change Orders' } // Hidden until feature is built
    ]
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: '📅',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'schedule-instances', label: 'Schedule Instances' },
      { id: 'schedule-planner', label: 'Planner' },
      { id: 'schedule-master-components', label: 'Master Component List' },
      { id: 'schedule-ga-dashboards', label: 'GA Dashboards' },
      { id: 'schedule-msp-export', label: 'MS Project' },
      { id: 'schedule-execution-plan', label: 'Execution Plan' },
      { id: 'schedule-analysis', label: 'Schedule Analysis' },
      { id: 'schedule-modules', label: 'Schedule options' },
      { id: 'schedule-lbs', label: 'LBS Schedule', requiresModule: 'lbs' },
      { id: 'schedule-model', label: 'Model' },
      { id: 'schedule-checklists', label: 'Checklists' }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: '📈',
    requiresProject: true,
    department: 'projects',
    subItems: [
      // Actuals / Budget / Earned folded into EVM (Resource Control)
      { id: 'evm-report', label: 'EVM Report' },
      { id: 'client-report', label: 'Client Report' },
      { id: 'sov-report', label: 'SOV Summary' }
    ]
  },
  {
    id: 'forecasting',
    label: 'Forecasting',
    icon: '🧭',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'forecasting-maestro-reports', label: 'Maestro' },
      { id: 'forecasting-dashboard1', label: 'Forecast' },
      { id: 'forecasting-billing', label: 'Billing' }
    ]
  },
  {
    id: 'materialManagement',
    label: 'Material Mngmnt',
    icon: '📦',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'material-log', label: 'Material Log' },
      { id: 'material-requisition-log', label: 'Requisition Log' },
      { id: 'tariff', label: 'Tariff' },
      { id: 'purchasing-list', label: 'Purchasing List' }
    ]
  },
  {
    id: 'changeManagement',
    label: 'Change Mngmnt',
    icon: '🔄',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'control-chart', label: 'Control Chart (Change Log)' }
    ]
  },
  {
    id: 'procurement-selected-contractors',
    label: 'Selected Contractors',
    icon: '🛒',
    hideInProject: true,
    department: 'procurement'
  },
  {
    // Master Material Management dashboard: every requisition sent to
    // purchasing from an MTO lands here (stock / PO / received workflow).
    id: 'material-management',
    label: 'Material Management',
    icon: '📦',
    hideInProject: true,
    department: 'procurement'
  },

  {
    // Same dashboard surfaced under Project Management, filtered by job number.
    id: 'material-management-projects',
    label: 'Material Management',
    icon: '📦',
    hideInProject: true,
    department: 'projects'
  },
  {
    id: 'resources-management',
    label: 'Indirect Resources',
    icon: '👥',
    hideInProject: true,
    department: 'resources'
  },
  {
    id: 'resources-fabrication',
    label: 'Corporation Resource Histogram',
    icon: '📊',
    hideInProject: true,
    department: 'resources'
  },
  {
    id: 'resources-portfolio-evm',
    label: 'Portfolio EVM',
    icon: '🎯',
    hideInProject: true,
    department: 'resources'
  },
  {
    id: 'technical',
    label: 'Technical',
    icon: '📐',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { id: 'technical-sheets', label: 'Sheets' },
      { id: 'technical-files', label: 'Files' },
      { id: 'technical-specifications', label: 'Specifications' },
      { id: 'technical-issues', label: 'Issues' },
      { id: 'technical-submittals', label: 'Submittals' },
      { id: 'technical-checklists', label: 'Checklists' },
      { id: 'technical-forms', label: 'Forms' },
      { id: 'technical-photos', label: 'Photos' },
      { id: 'technical-rfis', label: 'RFIs' },
      { id: 'technical-requisitions', label: 'Requisitions' },
      { id: 'technical-meetings', label: 'Meetings' },
      { id: 'technical-assets', label: 'Assets' },
      { id: 'technical-closeout', label: 'Closeout' },
      { id: 'technical-reports', label: 'Reports' },
      { id: 'technical-bridge', label: 'Bridge' }
    ]
  },
  {
    id: 'qa-qc',
    label: 'QA/QC',
    icon: '✅',
    requiresProject: true,
    department: 'projects',
    subItems: [
      { 
        id: 'qa-qc-fillable', 
        label: 'Fillable',
        subItems: [
          { id: 'qa-qc-fillable-concrete-inspection', label: 'Concrete Inspection' },
          { id: 'qa-qc-fillable-conveyors-feeders', label: 'Conveyors and Feeders' },
          { id: 'qa-qc-fillable-exhibit-b-incoming-inspection', label: 'Exhibit B Incoming Inspection' },
          { id: 'qa-qc-fillable-grout-inspection', label: 'Grout Inspection' },
          { id: 'qa-qc-fillable-heat-exchangers', label: 'Heat Exchangers' },
          { id: 'qa-qc-fillable-pipe-inspection-master', label: 'Pipe Inspection Form - Master' },
          { id: 'qa-qc-fillable-pumps', label: 'Pumps' },
          { id: 'qa-qc-fillable-screw-conveyors-feeders', label: 'Screw Conveyors and Feeders' },
          { id: 'qa-qc-fillable-structural-steel-completion', label: 'Structural Steel Completion Checklist' },
          { id: 'qa-qc-fillable-structural-steel-inspection', label: 'Structural Steel Inspection Checklist' },
          { id: 'qa-qc-fillable-tanks', label: 'Tanks' }
        ]
      },
      { id: 'qa-qc-checklists', label: 'Checklists' },
      { id: 'qa-qc-material-receiving-inspection', label: 'Material Receiving Inspection' },
      { id: 'qa-qc-piping-material-receiving-checklist', label: 'Piping Material Checklist' },
      { id: 'qa-qc-weld-fit-up-visual-inspection', label: 'Weld Fit-Up & Visual Inspection' },
      { id: 'qa-qc-ndt-weld-examination-report', label: 'NDT Weld Examination Report' },
      { id: 'qa-qc-piping-hydrostatic-test-report', label: 'Piping Hydrostatic Test' },
      { id: 'qa-qc-piping-pneumatic-test-report', label: 'Piping Pneumatic Test' },
      { id: 'qa-qc-piping-pre-commissioning-checklist', label: 'Pre-Commissioning Checklist' },
      { id: 'qa-qc-mechanical-completion-checklist', label: 'Mechanical Completion' },
      { id: 'qa-qc-punch-list-deficiency-list', label: 'Punch List / Deficiency List' },
      { id: 'qa-qc-non-conformance-report', label: 'Non-Conformance Report (NCR)' }
    ]
  },
  {
    id: 'quality-welder-continuity-log',
    label: 'Welder Continuity Log',
    icon: '👷',
    hideInProject: true,
    department: 'quality',
  },
  {
    id: 'quality-process-log',
    label: 'Process Log',
    icon: '🔧',
    hideInProject: true,
    department: 'quality',
  },
  // HSE department — flat root items (department selector already scopes to HSE).
  // Unfinished forms are gated by feature flags (pilot = builder); no (WIP) in labels.
  {
    id: 'hse-report',
    label: 'HSE Report',
    icon: '📊',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-training-db',
    label: 'Training Database',
    icon: '🎓',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-jha-jsa',
    label: 'Job Hazard Analysis (JHA/JSA)',
    icon: '⚠️',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-toolbox-talk',
    label: 'Toolbox Safety Talk',
    icon: '🗣️',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-daily-inspection',
    label: 'Daily Site Safety Inspection',
    icon: '🔍',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-ptw-piping-mechanical',
    label: 'Permit to Work (PTW)',
    icon: '📝',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-hot-work-permit',
    label: 'Hot Work Permit',
    icon: '🔥',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-incident-near-miss',
    label: 'Incident / Near-Miss Report',
    icon: '🚨',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'safety-certificates-db',
    label: 'Safety Certificates DB',
    icon: '📜',
    hideInProject: true,
    department: 'hse',
  },
  {
    id: 'training',
    label: 'Training',
    icon: '🎓',
    hideInProject: true,
    department: 'pmo',
    subItems: [
      { id: 'training-lgl-portal', label: 'LGL Portal' },
      {
        id: 'training-sharepoint',
        label: 'Sharepoint',
        subItems: [
          { id: 'training-sharepoint-naming', label: 'Naming Conventions' },
          { id: 'training-sharepoint-templates', label: 'Finding Templates' }
        ]
      },
      { id: 'training-build', label: 'Build' },
      { id: 'training-shortcuts', label: 'Keyboard Shortcuts' },
      { id: 'training-mouse-gestures', label: 'Mouse & Trackpad Tips' },
      { id: 'training-question-to-admin', label: 'Question to Admin' }
    ]
  },
  {
    id: 'processes',
    label: 'Processes',
    icon: '🔀',
    hideInProject: true,
    department: 'pmo',
    subItems: [
      { id: 'lgl-portal-tour', label: 'LGL Portal Tour' },
      { id: 'processes-permissions-guide', label: 'Permissions Guide (PDF)' },
      { id: 'processes-requisitions', label: 'Requisitions' },
      { id: 'processes-purchasing', label: 'Purchasing' },
      { id: 'processes-estimation', label: 'Estimation' },
      { id: 'processes-scheduling', label: 'Scheduling' },
      { id: 'processes-suggestion-box', label: 'Suggestion Box' }
    ]
  },
  {
    id: 'development',
    label: 'Development',
    icon: '🛠️',
    hideInProject: true,
    department: 'pmo',
    subItems: [
      { id: 'development-proposals', label: 'Proposals' },
      { id: 'development-log', label: 'Development Log' },
      { id: 'development-feature-flags', label: 'Feature Flags' },
      { id: 'development-portal-overview', label: 'Portal Overview Doc' },
      {
        id: 'debugging',
        label: 'Debugging',
        subItems: [
          { id: 'development-debugging', label: 'Debugging' }
        ]
      },
      { id: 'code-refactoring', label: 'Code Refactoring' },
      { 
        id: 'dashboards', 
        label: 'Dashboards', 
        subItems: [
          { id: 'dashboard-features-matrix', label: 'Features Matrix' },
          { id: 'dashboard-permissions', label: 'Permissions Dashboard' },
          // Email Log + Notification Recipients moved to Admin → Notifications (2026-08-12)
          { id: 'dashboard-infra-health', label: 'Infra Health' }
        ]
      },
      { 
        id: 'templates', 
        label: 'Templates', 
        subItems: [
          { id: 'template-dashboard', label: 'Dashboard Template' }
        ]
      },
      { 
        id: 'modules', 
        label: 'Modules', 
        subItems: [
          { id: 'modules-filtering-template', label: 'Filtering Template' },
          { id: 'modules-viewing-template', label: 'Viewing Template' },
          { id: 'modules-sorting-template', label: 'Sorting Template' },
          { id: 'modules-autofit-template', label: 'Autofit Template' },
          { id: 'modules-audit-log-template', label: 'Audit Log Template' },
          { id: 'modules-column-config-template', label: 'Column Config Template' },
          { id: 'modules-help-button-template', label: 'Help Button Template' },
          { id: 'modules-dashboard-template', label: 'Dashboard Template' },
          { id: 'modules-search-box-template', label: 'Search Box Template' },
          { id: 'modules-column-resize-template', label: 'Column Resize Template' },
          { id: 'modules-fill-down-template', label: 'Fill Down Template' }
        ]
      },
      { 
        id: 'test', 
        label: 'Test', 
        subItems: [
          { id: 'test-email-reminders', label: 'Email Reminder Tests' },
          { id: 'test-piping-progress-tracker', label: 'Piping Progress Tracker' },
          { id: 'test-mech-rot-eqp-tracker', label: 'Mech Rot Eqp Tracker' }
        ]
      },
      { id: 'file-upload', label: 'File Upload' },
      { 
        id: 'wip', 
        label: 'WIP', 
        subItems: [
          { id: 'wip-database-schema', label: 'Database Schema' },
          { id: 'wip-chatbot-audit', label: 'Chatbot Audit Log' },
          { id: 'wip-normal-distribution', label: 'Normal Distribution' },
          { id: 'estimation-piping-bom-extraction', label: 'BOM Extraction' },
          { id: 'estimation-piping-bom-extractor-api', label: 'BOM Extractor API' },
          { id: 'wip-spreadsheet-univer', label: 'Spreadsheet: Univer' },
          { id: 'wip-spreadsheet-handsontable', label: 'Spreadsheet: Handsontable' },
          { id: 'wip-spreadsheet-jspreadsheet', label: 'Spreadsheet: jspreadsheet' },
          { id: 'wip-employee-management', label: 'Employee Management Process' },
          { id: 'forecasting-budget', label: 'Forecasting: Budget' },
          { id: 'forecasting-actuals-accruals', label: 'Forecasting: Actuals + Accruals' },
          { id: 'forecasting-po', label: 'Forecasting: PO' },
          { id: 'forecasting-invoices', label: 'Forecasting: Invoices' },
          { id: 'forecasting-commitments', label: 'Forecasting: Commitments' },
          { id: 'forecasting-fwo', label: 'Forecasting: FWO' },
          { id: 'forecasting-co', label: 'Forecasting: CO' },
          { id: 'forecasting-ccr', label: 'Forecasting: CCR' },
          { id: 'forecasting-cash-flow', label: 'Forecasting: Cash Flow' },
          { id: 'forecasting-forecast', label: 'Forecasting: Forecast' },
          { id: 'forecasting-sop', label: 'Forecasting: SOP' }
        ]
      }
    ]
  },
  {
    id: 'clients-database',
    label: 'Clients Database',
    icon: '👥',
    hideInProject: true,
    department: 'sales'
  },
  {
    id: 'bid-log',
    label: 'Bid Log',
    icon: '📋',
    hideInProject: true,
    department: 'sales'
  },
  {
    id: 'roadmap',
    label: 'Roadmap',
    icon: '🗺️',
    hideInProject: true,
    department: 'pmo',
    subItems: [
      { id: 'pmo-radiator', label: 'Radiator (Burndown)' },
      { id: 'pmo-usage', label: 'Portal Usage' },
      { id: 'roadmap-erp', label: 'ERP Roadmap' },
      { id: 'roadmap-swimlane', label: 'Timeline' },
      { id: 'roadmap-strategy', label: 'Strategy' }
    ]
  },
  {
    id: 'subcontractor-discovery',
    label: 'Subcontractor Discovery',
    icon: '🔍',
    hideInProject: true,
    department: 'pmo',
  },
  {
    id: 'download-app',
    label: 'Download App',
    icon: '📲',
    hideInProject: true,
    department: 'pmo'
  }
];

/**
 * Get the label for a menu item by its ID
 * Recursively searches through the menu structure to find the matching item
 * @param {string} menuItemId - The menu item ID to search for
 * @param {Array} menuStructure - The menu structure to search in (defaults to MENU_STRUCTURE)
 * @returns {string|null} - The label of the menu item or null if not found
 */
export const getMenuItemLabel = (menuItemId, menuStructure = MENU_STRUCTURE) => {
  for (const section of menuStructure) {
    if (section.id === menuItemId) return section.label;
    if (section.subItems) {
      const result = getMenuItemLabel(menuItemId, section.subItems);
      if (result) return result;
    }
  }
  return null;
};

export default MENU_STRUCTURE;
